import React, { createElement, memo, useEffect, useRef } from 'react';
import { cn } from './cn';

// Memoized wrapper component that strips fill/stroke attributes after render
const IconWrapper = memo<{ icon: any; className: string; removeFill?: boolean; onClick?: () => void }>(
    ({ icon, className, removeFill = true, onClick }) => {
        const containerRef = useRef<HTMLSpanElement>(null);

        useEffect(() => {
            if (containerRef.current && removeFill) {
                const svg = containerRef.current.querySelector('svg');
                if (svg) {
                    const allElements = svg.querySelectorAll('*');
                    allElements.forEach(element => {
                        if (element instanceof SVGElement) {
                            element.removeAttribute('fill');
                            element.removeAttribute('stroke');
                        }
                    });
                    svg.setAttribute('fill', 'currentColor');
                }
            }
        }, [removeFill]);

        // Props for the icon (no ref!)
        const iconProps = {
            className,
            onClick,
        };

        let iconElement;
        if (typeof icon === 'function') {
            iconElement = createElement(icon, iconProps);
        } else if (typeof icon === 'object' && icon !== null) {
            if (icon.$$typeof === Symbol.for('react.element')) {
                iconElement = createElement(icon.type, {
                    ...icon.props,
                    className: cn(icon.props?.className, className),
                });
            } else if (
                icon.$$typeof === Symbol.for('react.forward_ref') ||
                icon.$$typeof === Symbol.for('react.memo')
            ) {
                iconElement = createElement(icon, iconProps);
            } else {
                iconElement = icon;
            }
        } else {
            iconElement = icon;
        }

        // Wrap in span with ref - simpler, no ref issues!
        return createElement('span', { ref: containerRef, style: { display: 'contents' } }, iconElement);
    }
);

IconWrapper.displayName = 'IconWrapper';

/**
 * Creates a React element from various icon input formats with optional className styling.
 * Automatically removes hardcoded fill/stroke attributes to ensure icons inherit parent color.
 *
 * This utility function provides flexible icon handling across the application, supporting
 * multiple input formats commonly used in React applications.
 *
 * @param {any} icon - The icon to render. Accepts multiple formats:
 *   - **React Element (JSX)**: Pre-rendered JSX like `<LuPlus />` or `<IconLink />`
 *     - Clones the element and merges the provided className with existing props
 *   - **Component Reference**: Direct component reference like `LuPlus` or `IconLink`
 *     - Creates a new element with the provided className
 *   - **SVG Import**: Vite SVG imports like `import Icon from '@/assets/icons/icon.svg'`
 *     - Handles forwardRef and memo wrapped components from SVG imports
 *   - **Other ReactNode**: Strings, numbers, or other valid React children
 *     - Returns as-is without modification
 *
 * @param {string} [className=''] - Optional CSS class names to apply to the icon element.
 *   - For JSX elements: Merges with existing className prop
 *   - For component references: Passes as className prop
 *   - For other types: Ignored
 *
 * @returns {React.ReactNode} The rendered icon element or the original value if not a component
 *
 * @example
 * // Using with JSX element (clones and merges className)
 * createIconElement(<LuPlus />, 'text-blue-500')
 *
 * @example
 * // Using with component reference (creates new element)
 * createIconElement(LuPlus, 'w-4 h-4')
 *
 * @example
 * // Using with SVG import (handles forwardRef)
 * import IconLink from '@/assets/icons/ic-link.svg';
 * createIconElement(IconLink, 'fill-current')
 *
 * @example
 * // Using in StanButton component
 * <StanButton icon={LuPlus}>Click me</StanButton>
 * // or
 * <StanButton icon={<LuPlus className="custom-class" />}>Click me</StanButton>
 */
export function createIconElement(
    icon: any,
    options?: {
        className?: string;
        removeFill?: boolean;
        onClick?: () => void;
    }
): React.ReactNode {
    if (!icon) return null;
    const { className, removeFill = true, onClick } = options ?? {};

    return createElement(IconWrapper, {
        icon,
        className: cn(className, onClick && 'cursor-pointer'),
        removeFill,
        onClick,
    });
}
